import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setApiToken, setUnauthorizedHandler } from "./lib/api";
import { can } from "./lib/permissions";

const TOKEN_KEY = "ulc_token";
const AUTH_URL = "https://auth.emergentagent.com/?redirect=";

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

const extractSessionId = (url) => url?.match(/[?#&]session_id=([^&#]+)/)?.[1] ?? null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const logout = useCallback(async () => {
    setApiToken(null);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const applySession = useCallback((res) => {
    localStorage.setItem(TOKEN_KEY, res.session_token);
    setApiToken(res.session_token);
    setAuthError("");
    setUser(res.user);
  }, []);

  const exchangeGoogle = useCallback(
    async (sessionId) => {
      try {
        const res = await api("/auth/session", {
          method: "POST",
          body: JSON.stringify({ session_id: sessionId }),
        });
        applySession(res);
        return true;
      } catch (e) {
        setAuthError(e.message);
        return false;
      }
    },
    [applySession]
  );

  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
    });
    (async () => {
      // 1) Google redirect carries a session_id.
      const sid = extractSessionId(window.location.hash) || extractSessionId(window.location.search);
      if (sid) {
        const ok = await exchangeGoogle(sid);
        const url = new URL(window.location.href);
        url.searchParams.delete("session_id");
        url.hash = url.hash
          .replace(/^#/, "")
          .split("&")
          .filter((p) => p && !p.startsWith("session_id="))
          .join("&");
        window.history.replaceState(window.history.state, "", url.toString());
        if (ok) return setLoading(false);
      }
      // 2) Restore an existing session.
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) {
        setApiToken(t);
        try {
          setUser(await api("/auth/me"));
        } catch {
          await logout();
        }
      }
      setLoading(false);
    })();
  }, [logout, exchangeGoogle]);

  const login = async (email, password) => {
    const res = await api("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    applySession({ session_token: res.token, user: res.user });
  };

  // Permissions change server-side without a new login; re-read them on focus.
  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    try { setUser(await api("/auth/me")); } catch (err) { console.error("Falha ao actualizar o utilizador", err); }
  }, []);

  useEffect(() => {
    const onFocus = () => refreshUser();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUser]);

  const loginWithGoogle = () => {
    setAuthError("");
    const redirect = window.location.origin + "/";
    window.location.href = AUTH_URL + encodeURIComponent(redirect);
  };

  return (
    <Ctx.Provider
      value={{ user, loading, authError, setAuthError, login, loginWithGoogle, logout, updateUser: setUser, refreshUser, can: (...p) => can(user, ...p) }}
    >
      {children}
    </Ctx.Provider>
  );
}
